import { ChevronDown, ChevronUp, Search, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { MuscleAssignment, MuscleGroup } from '@entities/exercise/model'
import { resolveMobileMuscleVisual } from '@shared/muscles/mobile-muscle-visuals'
import { ExerciseBodyMap } from './body-map'

const roleLabels: Record<MuscleAssignment['role'], string> = {
	PRIMARY: 'Основная',
	SECONDARY: 'Вторичная',
	STABILIZER: 'Стабилизатор',
}

export const MusclePicker = ({
	muscles,
	value,
	onChange,
	disabled = false,
}: {
	muscles: MuscleGroup[]
	value: MuscleAssignment[]
	onChange: (value: MuscleAssignment[]) => void
	disabled?: boolean
}) => {
	const [search, setSearch] = useState('')
	const selectedIds = new Set(value.map(item => item.muscle_group_id))
	const filtered = useMemo(() => {
		const normalized = search.trim().toLowerCase()
		return muscles.filter(muscle => {
			if (!muscle.active) return false
			if (!normalized) return true
			return [muscle.name, muscle.code, muscle.anatomical_region ?? '', muscle.description ?? '']
				.join(' ')
				.toLowerCase()
				.includes(normalized)
		})
	}, [muscles, search])
	const grouped = useMemo(() => {
		const result = new Map<string, MuscleGroup[]>()
		for (const muscle of filtered) {
			const region = muscle.anatomical_region?.trim() || 'Без региона'
			result.set(region, [...(result.get(region) ?? []), muscle])
		}
		return [...result.entries()].sort(([a], [b]) => a.localeCompare(b, 'ru'))
	}, [filtered])

	const add = (muscle: MuscleGroup) => {
		if (selectedIds.has(muscle.id)) return
		onChange([
			...value,
			{
				muscle_group_id: muscle.id,
				role: value.some(item => item.role === 'PRIMARY') ? 'SECONDARY' : 'PRIMARY',
				activation_level: value.length === 0 ? 100 : 60,
				order: value.length,
			},
		])
	}
	const update = (index: number, patch: Partial<MuscleAssignment>) =>
		onChange(value.map((item, current) => (current === index ? { ...item, ...patch } : item)))
	const remove = (index: number) =>
		onChange(value.filter((_, current) => current !== index).map((item, order) => ({ ...item, order })))
	const move = (index: number, direction: -1 | 1) => {
		const target = index + direction
		if (target < 0 || target >= value.length) return
		const next = [...value]
		;[next[index], next[target]] = [next[target], next[index]]
		onChange(next.map((item, order) => ({ ...item, order })))
	}

	return (
		<div className='grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]'>
			<div className='space-y-5'>
				<div className='relative'>
					<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
					<Input
						value={search}
						onChange={event => setSearch(event.target.value)}
						placeholder='Поиск по мышцам и регионам'
						className='pl-9'
					/>
				</div>
				<div className='max-h-80 space-y-4 overflow-y-auto rounded-xl border p-4'>
					{grouped.length === 0 ? (
						<p className='py-8 text-center text-sm text-muted-foreground'>Мышцы не найдены.</p>
					) : (
						grouped.map(([region, items]) => (
							<section key={region}>
								<h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
									{region}
								</h4>
								<div className='grid gap-2 sm:grid-cols-2'>
									{items.map(muscle => {
										const visual = resolveMobileMuscleVisual(muscle.code)
										return (
											<label
												key={muscle.id}
												className='flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-muted/50'>
												{visual ? (
													<img
														src={visual.image}
														alt=''
														className='h-14 w-14 shrink-0 rounded-md border bg-white object-contain'
														loading='lazy'
														decoding='async'
													/>
												) : null}
												<input
												type='checkbox'
												checked={selectedIds.has(muscle.id)}
												disabled={disabled}
												onChange={event => {
													if (event.target.checked) add(muscle)
													else remove(value.findIndex(item => item.muscle_group_id === muscle.id))
												}}
												className='mt-1 h-4 w-4 accent-primary'
											/>
											<span className='min-w-0'>
												<span className='block text-sm font-medium'>{muscle.name}</span>
												<span className='block truncate text-xs text-muted-foreground'>{muscle.code}</span>
											</span>
											</label>
										)
									})}
								</div>
							</section>
						))
					)}
				</div>
				<div className='space-y-3'>
					{value.length === 0 ? (
						<div className='rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground'>
							Выберите хотя бы одну основную мышцу.
						</div>
					) : (
						value.map((assignment, index) => {
							const muscle = muscles.find(item => item.id === assignment.muscle_group_id)
							return (
								<div key={assignment.muscle_group_id} className='rounded-xl border p-4'>
									<div className='flex flex-wrap items-start gap-3'>
										<div className='min-w-40 flex-1'>
											<p className='font-medium'>{muscle?.name ?? assignment.muscle_group_id}</p>
											<p className='text-xs text-muted-foreground'>{muscle?.anatomical_region}</p>
										</div>
										<select
											value={assignment.role}
											disabled={disabled}
											onChange={event => update(index, { role: event.target.value as MuscleAssignment['role'] })}
											className='h-10 rounded-md border bg-background px-3 text-sm'>
											{Object.entries(roleLabels).map(([role, label]) => (
												<option key={role} value={role}>{label}</option>
											))}
										</select>
										<div className='flex gap-1'>
											<Button type='button' variant='outline' size='icon' disabled={disabled || index === 0} onClick={() => move(index, -1)} aria-label='Переместить выше'>
												<ChevronUp className='h-4 w-4' />
											</Button>
											<Button type='button' variant='outline' size='icon' disabled={disabled || index === value.length - 1} onClick={() => move(index, 1)} aria-label='Переместить ниже'>
												<ChevronDown className='h-4 w-4' />
											</Button>
											<Button type='button' variant='ghost' size='icon' disabled={disabled} onClick={() => remove(index)} aria-label='Удалить мышцу'>
												<Trash2 className='h-4 w-4 text-red-600' />
											</Button>
										</div>
									</div>
									<label className='mt-4 block text-xs font-medium'>
										Активация: {assignment.activation_level ?? 0}%
										<input
											type='range'
											min={0}
											max={100}
											step={5}
											value={assignment.activation_level ?? 0}
											disabled={disabled}
											onChange={event => update(index, { activation_level: Number(event.target.value) })}
											className='mt-2 w-full accent-primary'
										/>
									</label>
								</div>
							)
						})
					)}
				</div>
			</div>
			<ExerciseBodyMap muscles={muscles} assignments={value} />
		</div>
	)
}
